import { attachApiErrorContext, createApiErrorFromResponse, readApiErrorContext } from "./api";
import type { ApiRequestMethod, SsoSessionApiClient } from "./oidc-session/types";

export type CenterOpenApiBaseUrlMode = "origin" | "service-root";

export type OpenApiClientResult<T = unknown> = {
  data?: T;
  error?: unknown;
  response: Response;
};

export type OpenApiResultOptions<T, R> = {
  emptyResponseError?: {
    code: string;
    message: string;
  };
  read?: (data: T) => R;
};

/**
 * Only the generic form participates in inference. A bare `OpenApiClientResult`
 * union member collapses `T` to `unknown` for every caller, which is why typed
 * call sites used to re-assert their generated response type with `as XxxRead`.
 */
type OpenApiResultInput<T> = OpenApiClientResult<T>;

export async function unwrapOpenApiResult<T = unknown, R = T>(
  result: OpenApiResultInput<T> | Promise<OpenApiResultInput<T>>,
  options: OpenApiResultOptions<T, R> = {},
): Promise<R> {
  const resolved = (await result) as OpenApiClientResult<T>;
  if (resolved.data !== undefined) {
    if (!options.read) {
      return resolved.data as unknown as R;
    }
    try {
      return options.read(resolved.data);
    } catch (error) {
      throw attachApiErrorContext(
        error,
        readApiErrorContext(resolved.response),
        resolved.response.status,
      );
    }
  }
  if (resolved.response.ok && resolved.error === undefined && options.emptyResponseError) {
    throw createApiErrorFromResponse(resolved.response, options.emptyResponseError);
  }
  throw createApiErrorFromResponse(resolved.response, resolved.error ?? resolved.response.statusText);
}

export async function unwrapOpenApiVoidResult<T = unknown>(
  result: OpenApiResultInput<T> | Promise<OpenApiResultInput<T>>,
): Promise<void> {
  const resolved = (await result) as OpenApiClientResult<T>;
  if (resolved.response.ok) {
    return;
  }
  throw createApiErrorFromResponse(resolved.response, resolved.error ?? resolved.response.statusText);
}

export type CenterOpenApiBaseUrlOptions = {
  mode: CenterOpenApiBaseUrlMode;
};

export function normalizeCenterOpenApiBaseUrl(
  baseUrl: string,
  options: CenterOpenApiBaseUrlOptions,
) {
  const normalizedBaseUrl = baseUrl.replace(/\/+$/, "");
  if (options.mode === "service-root") {
    const parsedBaseUrl = new URL(`${normalizedBaseUrl}/`);
    const basePath = parsedBaseUrl.pathname.replace(/\/+$/, "");
    if (/\/api(?:\/|$)/.test(basePath)) {
      throw new Error(
        `CENTER_OPENAPI_SERVICE_ROOT_CONTAINS_API_PATH: service root must not include /api, received ${basePath || "/"}`,
      );
    }
  }
  return normalizedBaseUrl;
}

export function resolveCenterOpenApiRequestPath(
  baseUrl: string,
  request: Request,
  options: CenterOpenApiBaseUrlOptions,
) {
  const normalizedBaseUrl = normalizeCenterOpenApiBaseUrl(baseUrl, options);
  const requestUrl = new URL(request.url);
  const base = new URL(`${normalizedBaseUrl}/`);
  const basePath = base.pathname.replace(/\/+$/, "");
  if (requestUrl.origin !== base.origin) {
    throw new Error(
      `CENTER_OPENAPI_REQUEST_ORIGIN_MISMATCH: expected ${base.origin}, received ${requestUrl.origin}`,
    );
  }
  if (
    basePath &&
    basePath !== "/" &&
    requestUrl.pathname !== basePath &&
    !requestUrl.pathname.startsWith(`${basePath}/`)
  ) {
    throw new Error(
      `CENTER_OPENAPI_REQUEST_PATH_MISMATCH: expected path under ${basePath}, received ${requestUrl.pathname}`,
    );
  }
  const pathname =
    basePath && basePath !== "/"
      ? requestUrl.pathname.slice(basePath.length) || "/"
      : requestUrl.pathname;
  if (
    basePath &&
    basePath !== "/" &&
    (pathname === basePath || pathname.startsWith(`${basePath}/`))
  ) {
    throw new Error(
      `CENTER_OPENAPI_REQUEST_PATH_DUPLICATED: generated path must own ${basePath}; received ${requestUrl.pathname}`,
    );
  }
  return `${pathname}${requestUrl.search}`;
}

export function requestHeadersToRecord(
  headersInit: HeadersInit | undefined,
  options: { dropContentType?: boolean } = {},
): Record<string, string> {
  const headers: Record<string, string> = {};
  if (!headersInit) {
    return headers;
  }
  new Headers(headersInit).forEach((value, key) => {
    if (options.dropContentType && key.toLowerCase() === "content-type") {
      return;
    }
    headers[key] = value;
  });
  return headers;
}

export function readCenterOpenApiRequestMethod(method: string): ApiRequestMethod {
  switch (method) {
    case "GET":
    case "POST":
    case "PUT":
    case "PATCH":
    case "DELETE":
      return method;
    default:
      throw new Error(`CENTER_OPENAPI_REQUEST_METHOD_UNSUPPORTED:${method || "missing-method"}`);
  }
}

export function isFormDataBody(body: unknown): body is FormData {
  return (
    typeof FormData !== "undefined" &&
    (body instanceof FormData || Object.prototype.toString.call(body) === "[object FormData]")
  );
}

export async function readCenterOpenApiRequestBody(request: Request) {
  if (request.method === "GET" || request.method === "HEAD" || request.body === null) {
    return undefined;
  }
  const contentType = request.headers.get("content-type") ?? "";
  if (contentType.includes("multipart/form-data")) {
    return await request.clone().formData();
  }
  if (!contentType.includes("application/json")) {
    throw new Error(
      `CENTER_OPENAPI_REQUEST_BODY_UNSUPPORTED:${contentType || "missing-content-type"}`,
    );
  }
  const text = await request.clone().text();
  if (!text) {
    return undefined;
  }
  return JSON.parse(text) as unknown;
}

export function createCenterOpenApiFetch(input: {
  baseUrl: string;
  client: SsoSessionApiClient;
  options: CenterOpenApiBaseUrlOptions;
}) {
  return async function centerOpenApiFetch(request: Request): Promise<Response> {
    const method = readCenterOpenApiRequestMethod(request.method);
    const body = await readCenterOpenApiRequestBody(request);
    return input.client.fetchWithAuth(
      resolveCenterOpenApiRequestPath(input.baseUrl, request, input.options),
      {
        body,
        headers: requestHeadersToRecord(request.headers, {
          dropContentType: body !== undefined,
        }),
        method,
      },
    );
  };
}

export function bindCenterOpenApiFetch(
  createClient: (baseUrl: string, fetchImpl?: typeof fetch) => SsoSessionApiClient,
  options: CenterOpenApiBaseUrlOptions,
) {
  return function boundCenterOpenApiFetch(
    baseUrl: string,
    fetchImpl: typeof fetch = fetch,
  ) {
    return createCenterOpenApiFetch({
      baseUrl,
      client: createClient(baseUrl, fetchImpl),
      options,
    });
  };
}
